import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import { useLocation, Navigate } from 'react-router-dom';

function ResetPassword() {
    const { register, handleSubmit, formState: { errors }, getValues, watch } = useForm();
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isValidToken, setIsValidToken] = useState(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const location = useLocation();
    
    const password = watch('password');
    
    // Get token from URL
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    
    // Password validation functions (same as PasswordMigration.js)
    const validatePassword = (value) => {
        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumbers = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
        const hasMinLength = value.length >= 8;
        
        if (!hasMinLength) return "Password must be at least 8 characters long";
        if (!hasUpperCase) return "Password must contain at least one uppercase letter";
        if (!hasLowerCase) return "Password must contain at least one lowercase letter";
        if (!hasNumbers) return "Password must contain at least one number";
        if (!hasSpecialChar) return "Password must contain at least one special character";
        return true;
    };
    
    // Password strength indicator (same as PasswordMigration.js)
    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, text: '', color: '' };
        
        let score = 0;
        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
        
        if (score <= 2) return { strength: score * 20, text: 'Weak', color: 'danger' };
        if (score <= 3) return { strength: score * 20, text: 'Fair', color: 'warning' };
        if (score <= 4) return { strength: score * 20, text: 'Good', color: 'info' };
        return { strength: score * 20, text: 'Strong', color: 'success' };
    };
    
    const passwordStrength = getPasswordStrength(password);
    
    // Validate token on component mount
    useEffect(() => {
        if (!token) {
            setError('Invalid reset link. Token is missing.');
            setIsValidToken(false);
            return;
        }
        
        axios.get(`/auth/validate-reset-token?token=${token}`)
            .then(response => {
                if (response.data.valid) {
                    setIsValidToken(true);
                } else {
                    setError('Invalid or expired reset token.');
                    setIsValidToken(false);
                }
            })
            .catch(err => {
                console.error('Token validation error:', err);
                setError('Error validating reset token. Please try again.');
                setIsValidToken(false);
            });
    }, [token]);
    
    function submit(data) {
        if (!token || !isValidToken) {
            setError('Invalid token. Cannot reset password.');
            return;
        }
        
        const bodyFormData = new FormData();
        bodyFormData.append("token", token);
        bodyFormData.append("newPassword", data.password);
        
        axios.post("/auth/reset-password", bodyFormData, {
            headers: {'content-type': 'application/x-www-form-urlencoded'}
        })
        .then(response => {
            if (response.status === 200) {
                setSuccess('Password reset successfully! You can now log in with your new password.');
                setTimeout(() => {
                    setIsCompleted(true);
                }, 3000);
            }
        })
        .catch(err => {
            console.error('Password reset error:', err);
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('An error occurred while resetting your password. Please try again.');
            }
        });
    }
    
    if (isCompleted) {
        return <Navigate to="/login" />;
    }
    
    if (isValidToken === null) {
        return (
            <div style={{ maxWidth: '500px', margin: '0 auto', padding: '20px' }}>
                <Card>
                    <Card.Body>
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Validating reset token...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }
    
    if (!isValidToken) {
        return (
            <div style={{ maxWidth: '500px', margin: '0 auto', padding: '20px' }}>
                <Card>
                    <Card.Body>
                        <Card.Title className="text-center mb-4">Invalid Reset Link</Card.Title>
                        <Alert variant="danger">{error}</Alert>
                        <div className="text-center">
                            <Button variant="primary" onClick={() => window.location.href = '/login'}>
                                Return to Login
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }
    
    return (
        <div style={{ maxWidth: '500px', margin: '0 auto', padding: '20px' }}>
            <Card>
                <Card.Body>
                    <Card.Title className="text-center mb-4">Reset Your Password</Card.Title>
                    
                    <Alert variant="info" className="mb-4">
                        Please enter your new password below. Make sure it meets all security requirements.
                    </Alert>
                    
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}
                    
                    <Form onSubmit={handleSubmit(submit)}>
                        {/* New Password */}
                        <Form.Group className="mb-3" controlId="formPassword">
                            <Form.Label>New Password *</Form.Label>
                            <Form.Control 
                                {...register("password", { 
                                    required: "Password is required",
                                    validate: validatePassword
                                })} 
                                type="password" 
                                placeholder="Enter new password" 
                            />
                            {password && (
                                <div className="mt-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <small>Password Strength: <span className={`text-${passwordStrength.color}`}>{passwordStrength.text}</span></small>
                                    </div>
                                    <div className="progress" style={{ height: '5px' }}>
                                        <div 
                                            className={`progress-bar bg-${passwordStrength.color}`}
                                            style={{ width: `${passwordStrength.strength}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                            <Form.Text className="text-muted">
                                Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                            </Form.Text>
                            {errors.password && <div className="text-danger mt-1">{errors.password.message}</div>}
                        </Form.Group>

                        {/* Confirm Password */}
                        <Form.Group className="mb-4" controlId="formPasswordConfirm">
                            <Form.Label>Confirm New Password *</Form.Label>
                            <Form.Control 
                                {...register("passwordConfirm", {
                                    required: "Please confirm your password",
                                    validate: value => value === getValues().password || "Passwords do not match"
                                })}
                                type="password" 
                                placeholder="Confirm new password" 
                            />
                            {errors.passwordConfirm && <div className="text-danger mt-1">{errors.passwordConfirm.message}</div>}
                        </Form.Group>

                        <div className="d-grid">
                            <Button variant="primary" type="submit" size="lg">
                                Reset Password
                            </Button>
                        </div>
                    </Form>
                    
                    <div className="text-center mt-3">
                        <Button variant="link" onClick={() => window.location.href = '/login'}>
                            Back to Login
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}

export default ResetPassword;